// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject, type WheelEvent as ReactWheelEvent } from 'react';

import { api } from '../../api/client';
import type { SolidSpace, SpaceDraft } from '../../api/types';
import type { GeometriEditor, GestureStatus } from '../../app/bygning/GeometriEditorContext';
import { useCanEdit } from '../../app/CaseRoleContext';
import { kindOf } from '../../app/keyTargets';
import { guideSnaps } from '../../bygning/cadMeasure';
import { lengthInput } from '../../bygning/geometriEdit';
import { flushKeys, resolveKeys, toolHint } from '../../bygning/geometriKeys';
import { infer, manhattanAngle, snapOffset, type Inference, type SnapSegment } from '../../bygning/inference';
import type { XY } from '../../bygning/planWalls';
import { gripLabel, insertVertex, moveEdge, moveVertex, polygonOps, removeVertex, roomGrips, type PolygonEdit, type RoomGrip } from '../../bygning/roomGrips';
import { closesPolygon, createRoomOps, draftOps, nextRoomName, placeSummary, splitSummary } from '../../bygning/roomTool';
import { formatAreaDa, labelAnchor, pointInPolygon, ringArea, spaceAt } from '../../bygning/spaces';
import { createZoneOps, toggleMemberOps, zoneLabel, zoneOutlines, zonesOf } from '../../bygning/zones';
import type { ViewportHandle } from '../../viewport/Viewport';
import type { TrackPoint } from '../../bygning/smartGuides';
import { Glyph, ScreenTip, TrackLines } from './GeometriLayer';
import { useSmartTracking } from './useSmartTracking';
import styles from './GeometriLayer.module.css';
import own from './RoomLayer.module.css';

/** A press that moves less than this (px) is a click. */
const DRAG_PX = 4;
/** The inference engine's reach, px. */
const SNAP_PX = 12;
/** A click this close (px) to the first corner closes the polygon. */
const CLOSE_PX = 10;
/** How long the pointer rests before Placér asks the server for the room under it, ms. */
const PLACE_WAIT_MS = 140;

export interface RoomLayerProps {
  viewport: RefObject<ViewportHandle | null>;
  host: RefObject<HTMLElement | null>;
  editor: GeometriEditor;
  storeyId: number | null;
  z: number;
}

/** Placér's preview under the pointer: the room the walls close there, or why there is none. */
interface PlaceHover {
  seed: XY;
  draft: SpaceDraft | null;
  existing: boolean;
  error: string | null;
}

/** A grip drag on a selected room. */
interface Drag {
  grip: RoomGrip;
  space: SolidSpace;
  moved: boolean;
  inf: Inference | null;
  edit: PolygonEdit | null;
}

const fmt = (v: number) => lengthInput(v);

/**
 * Rediger › Geometri's rooms and zones on the locked plan (frontend-u0o.6,
 * slice S6; mock 05), beside `GeometriLayer`, `CadToolLayer` and
 * `OpeningLayer`:
 *
 *  - the Rum tool (R): Placér i lukket område previews the room the walls
 *    close under the pointer and places it on a click; Tegn polygon draws
 *    one corner by corner with the inference engine (on wall faces, traces
 *    and guides), closed on the first corner or with Enter; Del rum cuts a
 *    room along a line (two clicks) and shows the pieces in the banner;
 *  - the Zone tool (Z): a click takes a room into the zone being edited, or
 *    out of it; with no zone picked the clicks gather a new one (Enter makes
 *    it);
 *  - a selected room's grips: drag a corner (inference) or an edge (square
 *    to itself, snapping to parallel faces); double-click a corner takes it
 *    out, an edge's grip puts a corner in;
 *  - the zones' dashed outlines and tags (under Rum / Zone, or when a
 *    selected room is in one), and the banner's previews (Fra vægge, Flet
 *    rum, Del rum).
 */
export function RoomLayer({ viewport, host: hostEl, editor, storeyId, z }: RoomLayerProps) {
  const [, setTick] = useState(0);
  const layerRef = useRef<HTMLDivElement>(null);
  const [pointer, setPointerState] = useState<{ raw: XY; inf: Inference | null; alt: boolean } | null>(null);
  const [points, setPointsState] = useState<XY[]>([]);
  const [place, setPlaceState] = useState<PlaceHover | null>(null);
  const [drag, setDragState] = useState<Drag | null>(null);
  const live = useRef({ editor, storeyId, z, pointer, points, place, drag });
  live.current = { editor, storeyId, z, pointer, points, place, drag };
  const setPointer = (p: typeof pointer) => {
    live.current.pointer = p;
    setPointerState(p);
  };
  const setPoints = (p: XY[]) => {
    live.current.points = p;
    setPointsState(p);
  };
  const setPlace = (p: PlaceHover | null) => {
    live.current.place = p;
    setPlaceState(p);
  };
  const setDrag = (d: Drag | null) => {
    live.current.drag = d;
    setDragState(d);
  };

  // A viewer may look (previews write nothing) but gets no tool and no grips; every apply goes through the editor, which refuses one too.
  const canEdit = useCanEdit();
  const model = editor.model;
  const busy = editor.modify !== null || !canEdit;
  const roomTool = editor.tool === 'room' && !busy && storeyId !== null;
  const zoneTool = editor.tool === 'zone' && !busy && storeyId !== null;
  const mode = editor.roomOptions.mode;

  // ------------------------------------------------------- projection --
  useEffect(() => {
    let frameId: number | null = null;
    const bump = () => {
      if (frameId !== null) return;
      frameId = requestAnimationFrame(() => {
        frameId = null;
        setTick((t) => t + 1);
      });
    };
    const off = viewport.current?.onCameraChange(bump) ?? (() => {});
    bump();
    return () => {
      off();
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, [viewport]);

  const toScreen = useCallback(
    (p: XY): [number, number] | null => {
      const s = viewport.current?.projectWorld(new Float64Array([p[0], p[1], live.current.z]), { clip: false });
      return s && Number.isFinite(s[0]) && Number.isFinite(s[1]) ? [s[0], s[1]] : null;
    },
    [viewport],
  );
  const metresPer = useCallback(
    (px: number): number => {
      const a = viewport.current?.planPoint(0, 0, live.current.z);
      const b = viewport.current?.planPoint(px, 0, live.current.z);
      return a && b ? Math.hypot(b[0] - a[0], b[1] - a[1]) : 0.1;
    },
    [viewport],
  );
  const planAtClient = useCallback(
    (clientX: number, clientY: number): XY | null => {
      const r = layerRef.current?.getBoundingClientRect();
      if (!r) return null;
      const hit = viewport.current?.planPoint(clientX - r.left, clientY - r.top, live.current.z);
      return hit ? [hit[0], hit[1]] : null;
    },
    [viewport],
  );

  // -------------------------------------------------- inference input --
  // A room's corners sit on wall faces, not axes: both faces of every wall on the storey, the traces and the guides.
  const snap = editor.snapTargets;
  const segments = useMemo<SnapSegment[]>(() => {
    const out: SnapSegment[] = [];
    for (const w of model.walls ?? []) {
      if (storeyId !== null && w.storey_id !== storeyId) continue;
      const L = Math.hypot(w.end[0] - w.start[0], w.end[1] - w.start[1]);
      if (!(L > 1e-6)) continue;
      const h = (w.thickness ?? 0) / 2;
      const n: XY = [(-(w.end[1] - w.start[1]) / L) * h, ((w.end[0] - w.start[0]) / L) * h];
      for (const k of h > 0 ? [1, -1] : [0]) {
        out.push({ a: [w.start[0] + n[0] * k, w.start[1] + n[1] * k], b: [w.end[0] + n[0] * k, w.end[1] + n[1] * k], source: 'wall', label: `Væg ${w.id} · flade`, guid: w.guid });
      }
    }
    for (const t of snap?.plane_traces ?? []) out.push({ a: t.start, b: t.end, source: 'trace', label: `P-${t.plane_label}`, points: t.point_count });
    return out;
  }, [model.walls, snap, storeyId]);
  const guides = useMemo(() => guideSnaps(editor.annotations.guides, storeyId), [editor.annotations.guides, storeyId]);
  const axisAngle = useMemo(() => manhattanAngle(snap?.axis_angles, segments), [snap, segments]);
  const ctxRef = useRef({ segments, guides, axisAngle });
  ctxRef.current = { segments, guides, axisAngle };
  // Smart guides' hover-acquired tracking points (frontend-u0o.12): per tool and mode, and per grip drag.
  const { tracked, points: trackedNow, observe } = useSmartTracking(`${editor.tool}:${editor.roomOptions.mode}`);
  /** The tool's own tracking points: the corners placed so far (or the ring's other corners, for a grip). */
  const inferAt = useCallback(
    (raw: XY, origin: XY | null, alt: boolean, auto: readonly TrackPoint[] = []): Inference | null => {
      const c = ctxRef.current;
      const inf = infer(raw, {
        segments: [...c.segments, ...c.guides.segments],
        points: c.guides.points,
        axisAngle: c.axisAngle,
        origin,
        toggles: live.current.editor.snap,
        tolerance: metresPer(SNAP_PX),
        suspended: alt,
        tracked: [...trackedNow(), ...auto],
      });
      observe(inf);
      return inf;
    },
    [metresPer, trackedNow, observe],
  );

  const storeySpaces = useMemo(() => (model.spaces ?? []).filter((s) => storeyId === null || s.storey_id === storeyId), [model.spaces, storeyId]);
  const storeyRef = useRef(storeySpaces);
  storeyRef.current = storeySpaces;

  // A new tool or mode starts afresh; so does an edit of the model (a stale preview would mislead).
  useEffect(() => {
    setPoints([]);
    setPlace(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor.tool, mode, storeyId]);
  useEffect(() => {
    setPlace(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.spaces, model.walls]);

  // ---------------------------------------------- Placér: the preview --
  const placeSeed = roomTool && mode === 'place' ? (pointer?.raw ?? null) : null;
  useEffect(() => {
    if (!placeSeed || storeyId === null) return;
    const cur = live.current.place;
    // Still inside the room already shown: no new question.
    if (cur?.draft && pointInPolygon(placeSeed[0], placeSeed[1], cur.draft.polygon)) return;
    const ctrl = new AbortController();
    const timer = setTimeout(() => {
      const ed = live.current.editor;
      ed.previewRooms((signal) => api.rebuildSpaces({ storey: storeyId, seed: [placeSeed[0], placeSeed[1]], preview: true }, signal), ctrl.signal)
        .then((res) => {
          if (ctrl.signal.aborted) return;
          if ('ok' in res) {
            const d = res.ok.space ?? res.ok.spaces[0] ?? null;
            setPlace({ seed: placeSeed, draft: d, existing: res.ok.existing === true, error: d ? null : 'Intet lukket område her' });
          } else setPlace({ seed: placeSeed, draft: null, existing: false, error: res.error });
        })
        .catch(() => {});
    }, PLACE_WAIT_MS);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [placeSeed, storeyId]);

  // ------------------------------------------------------- the clicks --
  const placeAt = useCallback(async (p: XY) => {
    const { editor: ed, storeyId: sid, place: cur } = live.current;
    if (sid === null) return;
    const shown = cur?.draft && pointInPolygon(p[0], p[1], cur.draft.polygon) ? cur : null;
    if (cur?.error && !shown && Math.hypot(cur.seed[0] - p[0], cur.seed[1] - p[1]) < metresPer(6)) {
      ed.notify(cur.error);
      return;
    }
    const opts = ed.roomOptions;
    const res = await ed.applyRooms(
      (base) => api.rebuildSpaces({ storey: sid, seed: [p[0], p[1]], preview: false, label: 'Placér rum', ...(base !== null ? { base_revision: base } : {}) }),
      'Placér rum',
      shown?.draft ? draftOps(ed.model, [shown.draft]) : [],
    );
    setPlace(null);
    const placed = res ? (res.space ?? res.spaces[0] ?? null) : null;
    if (!placed) return;
    // A new room comes back without a name: it takes the strip's (or the next "Rum N") in a step of its own.
    if (!res?.existing && placed.name.trim() === '') {
      const name = opts.name.trim() || nextRoomName(live.current.editor.model.spaces);
      void live.current.editor.commit([{ op: 'space.update', guid: placed.guid, set: { name, kind: opts.kind } }], `Navngiv ${name}`);
      if (opts.name.trim()) live.current.editor.setRoomOptions({ name: '' });
    }
    live.current.editor.select([placed.guid]);
  }, [metresPer]);

  const finishPolygon = useCallback(() => {
    const { editor: ed, storeyId: sid, points: pts } = live.current;
    if (sid === null) return;
    const guid = ed.newGuid();
    const r = createRoomOps(ed.model, pts, sid, ed.roomOptions, guid);
    if ('error' in r) {
      ed.notify(r.error);
      return;
    }
    setPoints([]);
    // The last corner's inference is done with.
    const cur = live.current.pointer;
    if (cur) setPointer({ ...cur, inf: null });
    if (ed.roomOptions.name.trim()) ed.setRoomOptions({ name: '' });
    void ed.commit(r.ops, r.label).then((ok) => ok && live.current.editor.select([guid]));
  }, []);

  const splitWith = useCallback(async (a: XY, b: XY) => {
    const { editor: ed } = live.current;
    const sel = ed.selection.length === 1 ? storeyRef.current.find((s) => s.guid === ed.selection[0]) : undefined;
    const mid: XY = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    const target = spaceAt(storeyRef.current, mid[0], mid[1]) ?? sel ?? null;
    if (!target) {
      ed.notify('Træk linjen hen over et rum');
      return;
    }
    const line: [XY, XY] = [a, b];
    const req = { guid: target.guid, line: [[a[0], a[1]], [b[0], b[1]]] as [[number, number], [number, number]] };
    const res = await ed.previewRooms((signal) => api.splitSpace({ ...req, preview: true }, signal));
    if ('error' in res) {
      ed.notify(res.error);
      return;
    }
    const pieces = res.ok.spaces;
    ed.setRoomPreview({
      kind: 'split',
      title: 'Del rum',
      summary: splitSummary(ed.model, target.guid, pieces),
      drafts: pieces,
      removed: [],
      line,
      apply: async () => {
        const done = await live.current.editor.applyRooms(
          (base) => api.splitSpace({ ...req, preview: false, label: 'Del rum', ...(base !== null ? { base_revision: base } : {}) }),
          'Del rum',
          draftOps(live.current.editor.model, pieces),
        );
        if (done) live.current.editor.select([done.spaces[0]?.guid ?? target.guid]);
        return done !== null;
      },
    });
  }, []);

  const pickZoneRoom = useCallback((p: XY) => {
    const { editor: ed } = live.current;
    const room = spaceAt(storeyRef.current, p[0], p[1]);
    if (!room) return;
    if (ed.zoneTarget && (ed.model.zones ?? []).some((zz) => zz.guid === ed.zoneTarget)) {
      const r = toggleMemberOps(ed.model, ed.zoneTarget, room.guid);
      if ('error' in r) ed.notify(r.error);
      else void ed.commit(r.ops, r.label);
      return;
    }
    const picks = ed.zonePicks.includes(room.guid) ? ed.zonePicks.filter((g) => g !== room.guid) : [...ed.zonePicks, room.guid];
    ed.setZonePicks(picks);
  }, []);

  const createPickedZone = useCallback(() => {
    const { editor: ed } = live.current;
    if (ed.zonePicks.length === 0) {
      ed.notify('Klik de rum, der skal være med i zonen');
      return;
    }
    const guid = ed.newGuid();
    const r = createZoneOps(ed.model, ed.zonePicks, guid, ed.roomOptions.zoneName, ed.roomOptions.zoneUse);
    if ('error' in r) {
      ed.notify(r.error);
      return;
    }
    ed.setZonePicks([]);
    ed.setZoneTarget(guid);
    ed.setRoomOptions({ zoneName: '', zoneUse: '' });
    void ed.commit(r.ops, r.label);
  }, []);

  // ------------------------------------------------ the tool's pointer --
  const toolOn = roomTool || zoneTool;
  useEffect(() => {
    const el = hostEl.current;
    if (!el || !toolOn) return;
    const originOf = () => {
      const { points: pts, editor: ed } = live.current;
      const m = ed.roomOptions.mode;
      return ed.tool === 'room' && (m === 'polygon' || m === 'split') && pts.length > 0 ? pts[pts.length - 1] : null;
    };
    const snapped = (e: PointerEvent): { raw: XY; inf: Inference | null } | null => {
      const raw = planAtClient(e.clientX, e.clientY);
      if (!raw) return null;
      const ed = live.current.editor;
      const drawing = ed.tool === 'room' && ed.roomOptions.mode !== 'place';
      const corners: TrackPoint[] = live.current.points.map((p, i) => ({ p, what: i === 0 ? 'endepunkt' : 'hjørne', of: 'Nyt rum', auto: true }));
      return { raw, inf: drawing ? inferAt(raw, originOf(), e.altKey, corners) : null };
    };
    const onMove = (e: PointerEvent) => {
      const s = snapped(e);
      if (!s) return;
      setPointer({ ...s, alt: e.altKey });
      live.current.editor.cursor.set(s.inf?.point ?? s.raw);
    };
    const onDown = (e: PointerEvent) => {
      if (e.button !== 0 || !(e.target instanceof HTMLCanvasElement)) return;
      e.stopPropagation();
      e.preventDefault();
      const s = snapped(e);
      if (!s) return;
      const ed = live.current.editor;
      const p = s.inf?.point ?? s.raw;
      if (ed.tool === 'zone') {
        pickZoneRoom(s.raw);
        return;
      }
      const m = ed.roomOptions.mode;
      if (m === 'place') void placeAt(s.raw);
      else if (m === 'polygon') {
        const pts = live.current.points;
        if (closesPolygon(pts, p, metresPer(CLOSE_PX))) finishPolygon();
        else setPoints([...pts, p]);
      } else {
        const pts = live.current.points;
        if (pts.length === 0) setPoints([p]);
        else {
          setPoints([]);
          setPointer({ raw: s.raw, inf: null, alt: false });
          void splitWith(pts[0], p);
        }
      }
    };
    const onLeave = () => setPointer(null);
    el.addEventListener('pointermove', onMove, true);
    el.addEventListener('pointerdown', onDown, true);
    el.addEventListener('pointerleave', onLeave);
    const prevCursor = el.style.cursor;
    el.style.cursor = 'crosshair';
    return () => {
      el.removeEventListener('pointermove', onMove, true);
      el.removeEventListener('pointerdown', onDown, true);
      el.removeEventListener('pointerleave', onLeave);
      el.style.cursor = prevCursor;
      setPointer(null);
    };
  }, [hostEl, toolOn, planAtClient, inferAt, metresPer, placeAt, finishPolygon, splitWith, pickZoneRoom]);

  // ------------------------------------------------- the tool's keys --
  const preview = editor.roomPreview;
  const keysOn = toolOn || preview !== null;
  useEffect(() => {
    if (!keysOn) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target instanceof HTMLElement ? e.target : null;
      if (target?.closest('input, textarea, select, [role="dialog"], [role="menu"]')) return;
      if (live.current.drag) return;
      const { editor: ed, points: pts } = live.current;
      const stop = () => {
        e.preventDefault();
        e.stopPropagation();
      };
      const plain = !e.metaKey && !e.ctrlKey && !e.altKey;
      // The banner first: Enter applies, Esc drops it.
      if (ed.roomPreview) {
        if (e.key === 'Enter') {
          stop();
          const p = ed.roomPreview;
          void p.apply().then((ok) => ok && live.current.editor.roomPreview === p && live.current.editor.setRoomPreview(null));
          return;
        }
        if (e.key === 'Escape') {
          stop();
          ed.setRoomPreview(null);
          return;
        }
        if (!(ed.tool === 'room' || ed.tool === 'zone')) return;
      }
      const tool = ed.tool;
      if (e.key === 'Escape') {
        stop();
        if (pts.length > 0) setPoints([]);
        else if (tool === 'zone' && ed.zonePicks.length > 0) ed.setZonePicks([]);
        else ed.setTool('select');
        return;
      }
      if (e.key === 'Enter') {
        stop();
        if (tool === 'zone') {
          // Editing a zone: Enter is done, the next clicks gather a new one.
          if (ed.zoneTarget) ed.setZoneTarget(null);
          else createPickedZone();
        }
        else if (ed.roomOptions.mode === 'polygon' && pts.length >= 3) finishPolygon();
        return;
      }
      if (e.key === 'Backspace' && plain && pts.length > 0) {
        stop();
        setPoints(pts.slice(0, -1));
        return;
      }
      // The rest is the editor's (tools' letters, ⌘Z, Z for a zone of the selected rooms): a letter acts alone.
      const out = resolveKeys({ key: e.key, metaKey: e.metaKey, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, altKey: e.altKey, kind: kindOf(target) }, '');
      const cmds = out.buffer ? [flushKeys(out.buffer)].filter((c) => c !== null) : out.commands;
      const run = cmds.filter((c) => c !== 'escape' && c !== 'cycle' && c !== 'cycle-back');
      if (run.length > 0 || (e.key.length === 1 && plain)) stop();
      for (const c of run) ed.run(c);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [keysOn, finishPolygon, createPickedZone]);

  // ------------------------------------------------- a selected room --
  const selectedGuid = editor.selection.length === 1 && editor.tool === 'select' && !editor.modify ? editor.selection[0] : null;
  const selected = useMemo(() => (selectedGuid ? (storeySpaces.find((s) => s.guid === selectedGuid && s.polygon.outer.length >= 3) ?? null) : null), [storeySpaces, selectedGuid]);
  const grips = selected && canEdit ? roomGrips(selected, metresPer(18)) : [];

  const evaluate = useCallback(
    (d: Drag, raw: XY, alt: boolean): Drag => {
      const g = d.grip;
      const ring = g.ring < 0 ? d.space.polygon.outer : d.space.polygon.holes[g.ring];
      if (g.kind === 'vertex') {
        const prev = ring[(g.index + ring.length - 1) % ring.length];
        const others: TrackPoint[] = ring.filter((_, i) => i !== g.index).map((q) => ({ p: [q[0], q[1]], what: 'hjørne', of: d.space.name ?? 'Rum', auto: true }));
        const inf = inferAt(raw, [prev[0], prev[1]], alt, others);
        return { ...d, inf, edit: moveVertex(d.space, g.ring, g.index, inf?.point ?? raw) };
      }
      const n = g.normal!;
      const rawOffset = (raw[0] - g.at[0]) * n[0] + (raw[1] - g.at[1]) * n[1];
      const c = ctxRef.current;
      const snapped = snapOffset(g.at, n, rawOffset, { segments: [...c.segments, ...c.guides.segments], toggles: live.current.editor.snap, tolerance: metresPer(SNAP_PX), suspended: alt });
      return { ...d, inf: snapped.inference, edit: moveEdge(d.space, g.ring, g.index, snapped.offset) };
    },
    [inferAt, metresPer],
  );

  const startGrip = (grip: RoomGrip, s: SolidSpace, e: ReactPointerEvent) => {
    if (e.button !== 0 || !canEdit) return;
    e.stopPropagation();
    e.preventDefault();
    // A double click: a corner out, or a corner in on an edge.
    if (e.detail >= 2) {
      const edit = grip.kind === 'vertex' ? removeVertex(s, grip.ring, grip.index) : insertVertex(s, grip.ring, grip.index);
      if ('error' in edit) editor.notify(edit.error);
      else {
        const ops = polygonOps(s, edit);
        if (ops) void editor.commit(ops, gripLabel(grip.kind === 'vertex' ? 'remove' : 'insert', s));
      }
      return;
    }
    const x0 = e.clientX;
    const y0 = e.clientY;
    setDrag({ grip, space: s, moved: false, inf: null, edit: null });
    const move = (m: PointerEvent) => {
      const cur = live.current.drag;
      if (!cur) return;
      if (!cur.moved && Math.hypot(m.clientX - x0, m.clientY - y0) < DRAG_PX) return;
      const raw = planAtClient(m.clientX, m.clientY);
      if (raw) setDrag(evaluate({ ...cur, moved: true }, raw, m.altKey));
    };
    const end = () => {
      window.removeEventListener('pointermove', move, true);
      window.removeEventListener('pointerup', up, true);
      window.removeEventListener('pointercancel', cancel, true);
      window.removeEventListener('keydown', esc, true);
    };
    const up = () => {
      end();
      const cur = live.current.drag;
      setDrag(null);
      if (!cur?.moved || !cur.edit) return;
      if ('error' in cur.edit) {
        live.current.editor.notify(cur.edit.error);
        return;
      }
      const ops = polygonOps(cur.space, cur.edit);
      if (ops) void live.current.editor.commit(ops, gripLabel(cur.grip.kind, cur.space));
    };
    const cancel = () => {
      end();
      setDrag(null);
    };
    const esc = (k: KeyboardEvent) => {
      if (k.key !== 'Escape') return;
      k.preventDefault();
      k.stopPropagation();
      cancel();
    };
    window.addEventListener('pointermove', move, true);
    window.addEventListener('pointerup', up, true);
    window.addEventListener('pointercancel', cancel, true);
    window.addEventListener('keydown', esc, true);
  };
  useEffect(() => {
    if (live.current.drag && live.current.drag.space.guid !== selectedGuid) setDrag(null);
  }, [selectedGuid]);

  // ------------------------------------------------------ status line --
  const drawPoint = pointer ? (pointer.inf?.point ?? pointer.raw) : null;
  const gesture = useMemo<GestureStatus | null>(() => {
    if (drag?.moved) {
      const what = drag.grip.kind === 'vertex' ? 'Hjørne' : 'Kant';
      const area = drag.edit && 'polygon' in drag.edit ? formatAreaDa(Math.abs(ringArea(drag.edit.polygon.outer))) : null;
      return { hint: { lead: `${what} · slip for at sætte`, keys: [{ key: 'Alt', text: 'uden snap' }, { key: 'Esc', text: 'annullér' }] }, readout: area ? `Areal ${area}` : null };
    }
    if (roomTool && mode === 'polygon' && points.length > 0) {
      const ring = drawPoint ? [...points, drawPoint] : points;
      const last = points[points.length - 1];
      const seg = drawPoint ? Math.hypot(drawPoint[0] - last[0], drawPoint[1] - last[1]) : 0;
      return {
        hint: {
          lead: points.length >= 3 ? 'Rum · klik det første hjørne eller ↵ for at lukke' : 'Rum · klik næste hjørne',
          keys: [{ key: '↵', text: 'luk' }, { key: '⌫', text: 'fortryd hjørne' }, { key: 'Alt', text: 'uden snap' }, { key: 'Esc', text: 'ryd' }],
        },
        readout: `${fmt(seg)} m · ${ring.length >= 3 ? formatAreaDa(Math.abs(ringArea(ring))) : `${points.length} hjørne${points.length === 1 ? '' : 'r'}`}`,
      };
    }
    if (roomTool && mode === 'split' && points.length > 0) {
      return { hint: { lead: 'Del rum · klik linjens andet punkt', keys: [{ key: 'Esc', text: 'ryd' }] }, readout: drawPoint ? `${fmt(Math.hypot(drawPoint[0] - points[0][0], drawPoint[1] - points[0][1]))} m` : null };
    }
    if (zoneTool) {
      const zt = editor.zoneTarget ? (model.zones ?? []).find((zz) => zz.guid === editor.zoneTarget) : undefined;
      if (zt) return { hint: { lead: `${zoneLabel(zt)} · klik rum for at tage dem med eller ud`, keys: [{ key: '↵', text: 'færdig' }, { key: 'Esc', text: 'afslut' }] }, readout: null };
      const n = editor.zonePicks.length;
      return {
        hint: { lead: n === 0 ? 'Zone · klik de rum, der skal med' : `Zone · ${n} rum valgt`, keys: [{ key: '↵', text: 'opret zone' }, { key: 'Esc', text: n > 0 ? 'ryd' : 'afslut' }] },
        readout: null,
      };
    }
    return null;
  }, [drag, roomTool, zoneTool, mode, points, drawPoint, editor.zoneTarget, editor.zonePicks, model.zones]);
  useEffect(() => {
    // While a tool is on, its keys are this layer's (a gesture holds the editor's own back); the hint is the tool's.
    editor.gesture.set(gesture ?? (toolOn ? { hint: toolHint(editor.tool, editor.selection.length, 'line', mode), readout: null } : null));
  }, [editor.gesture, gesture, toolOn, editor.tool, editor.selection.length, mode]);
  useEffect(() => () => live.current.editor.gesture.set(null), []);

  // ------------------------------------------------------------- draw --
  const ringPts = (ring: readonly (readonly number[])[]): string | null => {
    const out: string[] = [];
    for (const p of ring) {
      const s = toScreen([p[0], p[1]]);
      if (!s) return null;
      out.push(`${s[0].toFixed(1)},${s[1].toFixed(1)}`);
    }
    return out.join(' ');
  };
  const polyPath = (poly: { outer: readonly (readonly number[])[]; holes?: readonly (readonly (readonly number[])[])[] }): string | null => {
    const rings = [poly.outer, ...(poly.holes ?? [])];
    const parts: string[] = [];
    for (const r of rings) {
      const d = ringPts(r);
      if (!d) return null;
      parts.push(`M${d.split(' ').join('L')}Z`);
    }
    return parts.join('');
  };
  const chipAt = (poly: { outer: readonly (readonly [number, number])[]; holes: readonly (readonly (readonly [number, number])[])[] }) => {
    const a = labelAnchor(poly);
    return toScreen([a.x, a.y]);
  };

  // Zones: under Rum / Zone, or when a selected room is in one.
  const selectedZones = useMemo(() => new Set(editor.selection.flatMap((g) => zonesOf(model.zones, g).map((zz) => zz.guid))), [editor.selection, model.zones]);
  const outlines = useMemo(() => {
    // Over the walls between the rooms (and a little past the outer ones), square to the storey's axes.
    const all = zoneOutlines(model, storeyId, 0.2, axisAngle);
    return roomTool || zoneTool ? all : all.filter((o) => selectedZones.has(o.guid));
  }, [model, storeyId, roomTool, zoneTool, selectedZones, axisAngle]);

  const hoverRoom = zoneTool && pointer ? spaceAt(storeySpaces, pointer.raw[0], pointer.raw[1]) : null;
  const placeShown = roomTool && mode === 'place' && place && pointer ? place : null;
  const firstClose = roomTool && mode === 'polygon' && points.length >= 3 && drawPoint ? closesPolygon(points, drawPoint, metresPer(CLOSE_PX)) : false;
  const shownDrag = drag?.moved ? drag : null;
  const dragPoly = shownDrag?.edit && 'polygon' in shownDrag.edit ? shownDrag.edit.polygon : null;
  const shownInf = shownDrag ? shownDrag.inf : roomTool && mode !== 'place' ? (pointer?.inf ?? null) : null;
  const infAt = shownInf && shownInf.kind !== 'free' ? toScreen(shownInf.point) : null;
  const infGuide = shownInf?.guide ? ringPts([shownInf.guide[0], shownInf.guide[1]]) : null;

  // The wheel zooms the plan wherever the pointer is: over a grip or a chip too.
  const forwardWheel = (e: ReactWheelEvent) => {
    const canvas = hostEl.current?.querySelector('canvas');
    if (!canvas || e.target === canvas) return;
    canvas.dispatchEvent(new WheelEvent('wheel', { ...e.nativeEvent, deltaX: e.deltaX, deltaY: e.deltaY, deltaMode: e.deltaMode, clientX: e.clientX, clientY: e.clientY, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, bubbles: true, cancelable: true }));
  };

  return (
    <div ref={layerRef} className={styles.layer} onWheel={forwardWheel}>
      <svg className={styles.svg} aria-hidden="true">
        {/* Zones: a dashed outline round their rooms, in the zone's colour. */}
        {outlines.map((o) => {
          const d = polyPath({ outer: o.rings[0], holes: o.rings.slice(1) });
          return d ? <path key={o.guid} d={d} className={own.zone} style={{ stroke: `var(${o.swatch})` }} /> : null;
        })}
        {/* The banner's preview: Fra vægge's diff, Flet rum's union, Del rum's pieces and line. */}
        {preview?.drafts.map((dr, i) => {
          const d = polyPath(dr.polygon);
          if (!d) return null;
          const state = preview.states?.[dr.guid];
          const cls = preview.kind === 'rebuild' ? (state === 'new' ? own.draftNew : state === 'changed' ? own.draftChanged : own.draftSame) : preview.kind === 'split' && i % 2 === 1 ? own.draftAlt : own.draftNew;
          return <path key={`${dr.guid}${i}`} d={d} className={cls} />;
        })}
        {preview?.removed.map((g) => {
          const s = storeySpaces.find((x) => x.guid === g);
          const d = s ? polyPath(s.polygon) : null;
          return d ? <path key={`rm${g}`} d={d} className={own.removed} /> : null;
        })}
        {preview?.line && (() => {
          const d = ringPts(preview.line);
          return d ? <polyline points={d} className={styles.cut} /> : null;
        })()}
        {/* Placér: the room the walls close under the pointer. */}
        {placeShown?.draft && (() => {
          const d = polyPath(placeShown.draft.polygon);
          return d ? <path d={d} className={placeShown.existing ? own.draftChanged : own.draftNew} /> : null;
        })()}
        {/* The Zone tool: the room under the pointer. */}
        {hoverRoom && (() => {
          const d = polyPath(hoverRoom.polygon);
          return d ? <path d={d} className={own.hover} /> : null;
        })()}
        {/* Tegn polygon: the corners so far, the rubber band, the closing corner. */}
        {roomTool && mode === 'polygon' && points.length > 0 && (() => {
          const ring = drawPoint ? [...points, firstClose ? points[0] : drawPoint] : points;
          const d = ringPts(ring);
          const fill = ring.length >= 3 ? ringPts(firstClose ? points : ring) : null;
          return (
            <g>
              {fill && <polygon points={fill} className={own.drawFill} />}
              {d && <polyline points={d} className={styles.chainLine} />}
              {points.map((p, i) => {
                const s = toScreen(p);
                return s ? <circle key={i} cx={s[0]} cy={s[1]} r={i === 0 && firstClose ? 7 : 3.5} className={i === 0 && firstClose ? own.closeDot : styles.chainPoint} /> : null;
              })}
            </g>
          );
        })()}
        {/* Del rum: the line so far. */}
        {roomTool && mode === 'split' && points.length > 0 && drawPoint && (() => {
          const d = ringPts([points[0], drawPoint]);
          return d ? <polyline points={d} className={styles.cut} /> : null;
        })()}
        {/* A grip drag: the room as it will be. */}
        {dragPoly && (() => {
          const d = polyPath(dragPoly);
          return d ? <path d={d} className={own.draftChanged} /> : null;
        })()}
        {infGuide && shownInf && <polyline points={infGuide} className={shownInf.kind === 'trace' ? `${styles.guide} ${styles.traceGuide}` : styles.guide} />}
        {/* A selected room's grips: corners (circles), edges (bars along them). */}
        {selected &&
          grips.map((g) => {
            const s = toScreen(g.at);
            if (!s) return null;
            const on = drag?.grip.kind === g.kind && drag.grip.index === g.index && drag.grip.ring === g.ring;
            const cls = on ? `${styles.grip} ${styles.gripOn}` : styles.grip;
            const key = `${g.kind}${g.ring}:${g.index}`;
            if (g.kind === 'vertex') {
              return (
                <circle key={key} cx={s[0]} cy={s[1]} r={5} className={cls} onPointerDown={(e) => startGrip(g, selected, e)}>
                  <title>Træk hjørnet · dobbeltklik fjerner det</title>
                </circle>
              );
            }
            const t = toScreen([g.at[0] + g.normal![1], g.at[1] - g.normal![0]]);
            const angle = t ? (Math.atan2(t[1] - s[1], t[0] - s[0]) * 180) / Math.PI : 0;
            return (
              <rect key={key} x={s[0] - 7} y={s[1] - 2.5} width={14} height={5} rx={2} transform={`rotate(${angle.toFixed(1)} ${s[0]} ${s[1]})`} className={cls} onPointerDown={(e) => startGrip(g, selected, e)}>
                <title>Træk kanten vinkelret · dobbeltklik sætter et hjørne</title>
              </rect>
            );
          })}
        {shownInf && <TrackLines tracks={shownInf.tracks} points={tracked} toScreen={toScreen} />}
        {infAt && shownInf && <Glyph kind={shownInf.kind} at={infAt} />}
      </svg>
      {/* Zone tags at the top of each outline. */}
      {outlines.map((o) => {
        let top: [number, number] | null = null;
        for (const p of o.rings.flat()) {
          const s = toScreen(p);
          if (s && (!top || s[1] < top[1] - 0.5 || (Math.abs(s[1] - top[1]) <= 0.5 && s[0] < top[0]))) top = s;
        }
        return top ? (
          <span key={o.guid} className={own.zoneTag} style={{ background: `var(${o.swatch})`, transform: `translate(${top[0].toFixed(1)}px, ${(top[1] - 4).toFixed(1)}px) translate(0, -100%)` }}>
            {o.tag}
          </span>
        ) : null;
      })}
      {placeShown?.draft && (() => {
        const at = chipAt(placeShown.draft.polygon);
        return at ? (
          <span className={own.chip} style={{ transform: `translate(${at[0].toFixed(1)}px, ${(at[1] + 30).toFixed(1)}px) translate(-50%, 0)` }}>
            {placeSummary(placeShown.draft, placeShown.existing)} · klik for at placere
          </span>
        ) : null;
      })()}
      {placeShown && !placeShown.draft && placeShown.error && pointer && (() => {
        const at = toScreen(pointer.raw);
        return at ? (
          <span className={`${styles.tip} ${styles.tipWarn}`} style={{ transform: `translate(${(at[0] + 14).toFixed(1)}px, ${(at[1] + 14).toFixed(1)}px)` }}>
            {placeShown.error}
          </span>
        ) : null;
      })()}
      {infAt && shownInf?.tip && <ScreenTip inf={shownInf} at={infAt} />}
    </div>
  );
}
